import { useState } from 'react';
import { Mail, Phone, MapPin, Clock, Send, Loader2, CheckCircle2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Reveal } from '../components/Reveal';
import { COMPANY, SERVICE_AREAS } from '../lib/site';
import { submitContact } from '../lib/api';

const ContactPage = () => {
    const [form, setForm] = useState({ name: '', email: '', phone: '', subject: 'General enquiry', message: '' });
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error('Please add your name, email and a message.');
            return;
        }
        setLoading(true);
        try {
            await submitContact(form);
            toast.success('Message received — we\'ll be in touch within the hour.');
            setDone(true);
        } catch {
            toast.error('Could not send. Please try again or call us.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <main data-testid="contact-page">
            <section className="pt-16 md:pt-24 pb-12 border-b border-border">
                <div className="container-axion">
                    <Reveal>
                        <div className="overline">Contact</div>
                        <h1 className="font-outfit font-medium tracking-tight mt-4 text-5xl md:text-6xl lg:text-[5rem] leading-[1.02] max-w-4xl">
                            Let's <em className="italic font-light text-accent">talk cleaning.</em>
                        </h1>
                        <p className="mt-7 max-w-2xl text-muted-foreground text-lg leading-relaxed">
                            Drop us a message, ring our Bristol office, or send a WhatsApp — we reply
                            within the hour, Monday to Saturday.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="py-20 md:py-28">
                <div className="container-axion grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <Reveal className="lg:col-span-5 space-y-6">
                        <div className="overline">Direct</div>
                        <ul className="space-y-5">
                            <li className="flex items-start gap-4">
                                <span className="grid place-items-center w-10 h-10 rounded-full bg-muted text-primary">
                                    <Mail className="w-4 h-4" strokeWidth={1.6} />
                                </span>
                                <div>
                                    <div className="text-xs text-muted-foreground tracking-wider uppercase">Email</div>
                                    <a href={`mailto:${COMPANY.email}`} data-testid="contact-email" className="font-outfit text-lg text-foreground hover:text-primary">{COMPANY.email}</a>
                                </div>
                            </li>
                            <li className="flex items-start gap-4">
                                <span className="grid place-items-center w-10 h-10 rounded-full bg-muted text-primary">
                                    <Phone className="w-4 h-4" strokeWidth={1.6} />
                                </span>
                                <div>
                                    <div className="text-xs text-muted-foreground tracking-wider uppercase">Phone</div>
                                    <a href={`tel:${COMPANY.phone}`} data-testid="contact-phone" className="font-outfit text-lg text-foreground hover:text-primary">{COMPANY.phoneDisplay}</a>
                                </div>
                            </li>
                            <li className="flex items-start gap-4">
                                <span className="grid place-items-center w-10 h-10 rounded-full bg-muted text-primary">
                                    <MapPin className="w-4 h-4" strokeWidth={1.6} />
                                </span>
                                <div>
                                    <div className="text-xs text-muted-foreground tracking-wider uppercase">Service area</div>
                                    <div className="font-outfit text-lg text-foreground">{COMPANY.region}</div>
                                </div>
                            </li>
                            <li className="flex items-start gap-4">
                                <span className="grid place-items-center w-10 h-10 rounded-full bg-muted text-primary">
                                    <Clock className="w-4 h-4" strokeWidth={1.6} />
                                </span>
                                <div>
                                    <div className="text-xs text-muted-foreground tracking-wider uppercase">Hours</div>
                                    <div className="font-outfit text-lg text-foreground">{COMPANY.hours}</div>
                                </div>
                            </li>
                        </ul>

                        <div className="pt-8 border-t border-border">
                            <div className="overline">We cover</div>
                            <div className="mt-5 flex flex-wrap gap-2">
                                {SERVICE_AREAS.map((a) => (
                                    <span key={a} className="text-xs font-outfit border border-border rounded-full px-3 py-1.5 bg-white text-foreground">
                                        {a}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </Reveal>

                    <Reveal delay={140} className="lg:col-span-7">
                        <div className="bg-white border border-border rounded-2xl p-7 md:p-10">
                            {done ? (
                                <div className="py-10 text-center flex flex-col items-center gap-4" data-testid="contact-success">
                                    <span className="grid place-items-center w-14 h-14 rounded-full bg-accent/20 text-primary">
                                        <CheckCircle2 className="w-7 h-7" strokeWidth={1.6} />
                                    </span>
                                <div>
                                    <h3 className="font-outfit text-2xl">Message received</h3>
                                    <p className="text-muted-foreground max-w-md mt-2">
                                        Thank you — a real human in our Bristol office will reply within the hour.
                                    </p>
                                </div>
                                </div>
                            ) : (
                                <form onSubmit={onSubmit} className="space-y-5" data-testid="contact-form">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div className="space-y-1.5">
                                            <Label className="text-xs text-muted-foreground">Name</Label>
                                            <Input data-testid="contact-input-name" value={form.name} onChange={onChange('name')} required placeholder="Your full name" />
                                        </div>
                                        <div className="space-y-1.5">
                                            <Label className="text-xs text-muted-foreground">Email</Label>
                                            <Input data-testid="contact-input-email" type="email" value={form.email} onChange={onChange('email')} required placeholder="you@email.com" />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div className="space-y-1.5">
                                            <Label className="text-xs text-muted-foreground">Phone (optional)</Label>
                                            <Input data-testid="contact-input-phone" value={form.phone} onChange={onChange('phone')} placeholder="07xxx xxxxxx" />
                                        </div>
                                        <div className="space-y-1.5">
                                            <Label className="text-xs text-muted-foreground">Subject</Label>
                                            <Input data-testid="contact-input-subject" value={form.subject} onChange={onChange('subject')} />
                                        </div>
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label className="text-xs text-muted-foreground">Message</Label>
                                        <Textarea data-testid="contact-input-message" rows={5} value={form.message} onChange={onChange('message')} required placeholder="Tell us about your space and what you need." />
                                    </div>
                                    <Button
                                        type="submit"
                                        data-testid="contact-form-submit"
                                        disabled={loading}
                                        className="rounded-full bg-primary text-primary-foreground font-outfit h-12 px-7 hover:-translate-y-0.5 transition-transform"
                                    >
                                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Send message</>}
                                    </Button>
                                </form>
                            )}
                        </div>
                    </Reveal>
                </div>
            </section>
        </main>
    );
};

export default ContactPage;
